<template>
    <div v-if="price && price > 0" class="mt-2 flex items-center justify-between">
        <div class="flex flex-col">
            <span class="price text-4xl font-extrabold" v-text="formatPrice(price / 100)" data-test-id="pdp-price" />
            <span v-if="recommendedPrice && recommendedPrice > 0 && recommendedPrice > price" class="flex items-center gap-1">
                <span v-text="$t('general.prices.from.label')" />
                <span v-text="formatPrice(recommendedPrice / 100)" data-test-id="pdp-recommended-price" class="line-through" />
            </span>
        </div>

        <DiscountBadge :discount="discount" size="3xl" />
    </div>
</template>

<script setup lang="ts">
import DiscountBadge from '@/components/badges/DiscountBadge.vue';
import { useFormatPrice } from '@/composables/useFormatPrice';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import { useProductStore } from '@/stores/products/ProductStore';
import { computed } from 'vue';

const formatPrice = useFormatPrice();

const productPageStore = useProductPageStore();
const productStore = useProductStore();

const productData = computed(() => productStore.selectedProduct.product.data);

const price = computed<number | null>(() => (productData.value.product_price ? productData.value.product_price * 100 : null));
const recommendedPrice = computed<number | null>(() =>
    productData.value.product_price_recommended ? productData.value.product_price_recommended * 100 : null,
);
const regularPrice = computed<number | null>(() => (productData.value.product_price_regular ? productData.value.product_price_regular * 100 : null));

/** A real per-SKU discount lowered the price below what it sold for a moment ago. */
const hasActiveDiscount = computed(() => !!regularPrice.value && !!price.value && regularPrice.value > price.value);

/** A surcharge (legacy "Plus") raised the price; it is a plain higher price, never a badge. */
const hasActiveSurcharge = computed(() => !!regularPrice.value && !!price.value && regularPrice.value < price.value);

/** Measured against the struck-through price beside it; falls back to the CP's own manual badge when no price rule is active. */
const discount = computed(() => {
    if (hasActiveSurcharge.value) {
        return null;
    }

    if (!hasActiveDiscount.value || !recommendedPrice.value || !price.value) {
        return productPageStore.configurableProduct.discount;
    }

    return Math.round(((recommendedPrice.value - price.value) / recommendedPrice.value) * 100);
});
</script>
